/* ZakelijkDashboardPage - Ingelogd zakelijk portaal met prijslijst download + bestelformulier.
   Updated: Prijslijst wordt nu als bestand gedownload (blob) i.p.v. in een nieuw tabblad geopend, dat door pop-upblokkers werd tegengehouden.
   Foutafhandeling bij het vernieuwen van de bestellijst na het plaatsen van een bestelling.
   Ondersteunt meerdere actieve prijslijsten tegelijk — alle actieve lijsten worden getoond met aparte downloadknoppen. */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Loader as Loader2, Download, FileText, LogOut, Plus, Trash2,
  Package, Calendar, Truck, Store, CheckCircle2, Building2, User,
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';

interface BusinessCustomer {
  id: string;
  company_name: string;
  contact_first_name: string;
  contact_last_name: string;
  email: string;
  kvk_number: string;
  btw_number: string | null;
  phone: string;
  city: string;
  street: string;
  house_number: string;
  postal_code: string;
  country: string;
  status: string;
}

interface Pricelist {
  id: string;
  filename: string;
  storage_path: string;
  file_size: number | null;
  description: string | null;
  created_at: string;
}

interface OrderItem {
  product_name: string;
  quantity: string;
  notes: string;
}

interface BusinessOrder {
  id: string;
  order_number: string;
  items: OrderItem[];
  delivery_method: string;
  desired_delivery_date: string | null;
  pickup_date: string | null;
  notes: string | null;
  status: string;
  created_at: string;
}

export default function ZakelijkDashboardPage() {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<BusinessCustomer | null>(null);
  const [pricelists, setPricelists] = useState<Pricelist[]>([]);
  const [orders, setOrders] = useState<BusinessOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedOrder, setSubmittedOrder] = useState<string | null>(null);

  // Order form state
  const [orderItems, setOrderItems] = useState<OrderItem[]>([
    { product_name: '', quantity: '1', notes: '' },
  ]);
  const [deliveryMethod, setDeliveryMethod] = useState('bezorgen');
  const [desiredDeliveryDate, setDesiredDeliveryDate] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [orderNotes, setOrderNotes] = useState('');

  const loadData = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/zakelijk/inloggen');
        return;
      }

      const { data: custData, error: custError } = await supabase
        .from('business_customers')
        .select('id, company_name, contact_first_name, contact_last_name, email, kvk_number, btw_number, phone, city, street, house_number, postal_code, country, status')
        .eq('auth_user_id', user.id)
        .maybeSingle();

      if (custError || !custData) {
        navigate('/zakelijk/inloggen');
        return;
      }

      if (custData.status === 'pending' || custData.status === 'rejected' || custData.status === 'blocked') {
        await supabase.auth.signOut();
        navigate('/zakelijk/inloggen');
        return;
      }

      setCustomer(custData as BusinessCustomer);

      const [pricelistRes, ordersRes] = await Promise.all([
        supabase.from('business_pricelists').select('*').eq('is_active', true).order('created_at', { ascending: false }),
        supabase.from('business_orders').select('*').eq('business_customer_id', custData.id).order('created_at', { ascending: false }),
      ]);

      if (pricelistRes.data) setPricelists(pricelistRes.data as Pricelist[]);
      if (ordersRes.data) setOrders(ordersRes.data as BusinessOrder[]);
    } catch {
      navigate('/zakelijk/inloggen');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/zakelijk/inloggen');
  };

  const handleDownload = async (pl: Pricelist) => {
    setDownloadingId(pl.id);

    try {
      const { data, error } = await supabase.storage
        .from('business-pricelists')
        .download(pl.storage_path);

      if (error || !data) {
        toast.error('Kon de prijslijst niet ophalen');
        return;
      }

      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = pl.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      toast.error('Er is een fout opgetreden');
    } finally {
      setDownloadingId(null);
    }
  };

  const addOrderItem = () => {
    setOrderItems(prev => [...prev, { product_name: '', quantity: '1', notes: '' }]);
  };

  const removeOrderItem = (index: number) => {
    setOrderItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateOrderItem = (index: number, field: keyof OrderItem, value: string) => {
    setOrderItems(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const handleOrderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validItems = orderItems.filter(item => item.product_name.trim() && parseInt(item.quantity) > 0);
    if (validItems.length === 0) {
      toast.error('Voeg minimaal één geldig product toe');
      return;
    }

    if (deliveryMethod === 'bezorgen' && !desiredDeliveryDate) {
      toast.error('Vul een gewenste leverdatum in');
      return;
    }
    if (deliveryMethod === 'afhalen' && !pickupDate) {
      toast.error('Vul een afhaaldatum in');
      return;
    }

    setSubmitting(true);

    try {
      const { data, error } = await supabase
        .from('business_orders')
        .insert({
          business_customer_id: customer!.id,
          items: validItems,
          delivery_method: deliveryMethod,
          desired_delivery_date: deliveryMethod === 'bezorgen' ? desiredDeliveryDate : null,
          pickup_date: deliveryMethod === 'afhalen' ? pickupDate : null,
          notes: orderNotes.trim() || null,
        })
        .select('order_number')
        .maybeSingle();

      if (error) throw error;

      setSubmittedOrder(data?.order_number || null);
      setShowOrderForm(false);
      setOrderItems([{ product_name: '', quantity: '1', notes: '' }]);
      setOrderNotes('');
      setDesiredDeliveryDate('');
      setPickupDate('');
      toast.success('Bestelling verzonden!');

      // Reload orders
      const { data: ordersData, error: ordersError } = await supabase
        .from('business_orders')
        .select('*')
        .eq('business_customer_id', customer!.id)
        .order('created_at', { ascending: false });
      if (ordersError) {
        toast.error('Bestelling geplaatst, maar kon de lijst niet vernieuwen');
      } else if (ordersData) {
        setOrders(ordersData as BusinessOrder[]);
      }
    } catch {
      toast.error('Er is een fout opgetreden bij het verzenden');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('nl-NL', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      new: 'Nieuw',
      in_review: 'In behandeling',
      confirmed: 'Bevestigd',
      cancelled: 'Geannuleerd',
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      new: 'bg-blue-100 text-blue-800 border-blue-200',
      in_review: 'bg-amber-100 text-amber-800 border-amber-200',
      confirmed: 'bg-green-100 text-green-800 border-green-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fcfaf6] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#5a6552]" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Zakelijk Portaal | Buitn Planten</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Header bar */}
      <header className="bg-[#1e2b22] text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/assets/logos/buitnplanten-logo-solo-beige.webp"
              alt="Buitn Planten"
              className="h-8 w-auto"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <span className="text-sm font-medium tracking-wide">Zakelijk Portaal</span>
          </div>
          <Button
            onClick={handleSignOut}
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Uitloggen
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Welcome */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-bold text-[#1e2b22] mb-1">
            Welkom, {customer?.contact_first_name}
          </h1>
          <p className="text-[#5a6552]">{customer?.company_name}</p>
        </motion.div>

        {/* Company info card */}
        <div className="bg-white rounded-2xl border border-[#e7e1d6] p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-5 w-5 text-[#5a6552]" />
            <h2 className="text-lg font-semibold text-[#1e2b22]">Bedrijfsgegevens</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-[#5a6552] text-xs uppercase tracking-wide mb-1">Bedrijfsnaam</p>
              <p className="text-[#1e2b22] font-medium">{customer?.company_name}</p>
            </div>
            <div>
              <p className="text-[#5a6552] text-xs uppercase tracking-wide mb-1">Contactpersoon</p>
              <p className="text-[#1e2b22] font-medium">{customer?.contact_first_name} {customer?.contact_last_name}</p>
            </div>
            <div>
              <p className="text-[#5a6552] text-xs uppercase tracking-wide mb-1">E-mail</p>
              <p className="text-[#1e2b22] font-medium">{customer?.email}</p>
            </div>
            <div>
              <p className="text-[#5a6552] text-xs uppercase tracking-wide mb-1">KVK-nummer</p>
              <p className="text-[#1e2b22] font-medium">{customer?.kvk_number}</p>
            </div>
            <div>
              <p className="text-[#5a6552] text-xs uppercase tracking-wide mb-1">BTW-nummer</p>
              <p className="text-[#1e2b22] font-medium">{customer?.btw_number || '-'}</p>
            </div>
            <div>
              <p className="text-[#5a6552] text-xs uppercase tracking-wide mb-1">Telefoon</p>
              <p className="text-[#1e2b22] font-medium">{customer?.phone}</p>
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <p className="text-[#5a6552] text-xs uppercase tracking-wide mb-1">Adres</p>
              <p className="text-[#1e2b22] font-medium">
                {customer?.street} {customer?.house_number}, {customer?.postal_code} {customer?.city}, {customer?.country}
              </p>
            </div>
          </div>
        </div>

        {/* Pricelist section */}
        <div className="bg-white rounded-2xl border border-[#e7e1d6] p-6 mb-8">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-5 w-5 text-[#5a6552]" />
            <h2 className="text-lg font-semibold text-[#1e2b22]">Zakelijke prijslijsten</h2>
          </div>

          {pricelists.length > 0 ? (
            <div className="space-y-3">
              {pricelists.map((pl) => (
                <div key={pl.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-[#fcfaf6] rounded-xl border border-[#e7e1d6] p-5">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#1e2b22]/5 border border-[#e7e1d6]">
                      <FileText className="h-6 w-6 text-[#1e2b22]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#1e2b22]">{pl.filename}</p>
                      <p className="text-sm text-[#5a6552]">
                        PDF {pl.file_size ? `· ${formatFileSize(pl.file_size)}` : ''} · Bijgewerkt op {formatDate(pl.created_at)}
                      </p>
                      {pl.description && (
                        <p className="text-sm text-[#5a6552] mt-0.5">{pl.description}</p>
                      )}
                    </div>
                  </div>
                  <Button
                    onClick={() => handleDownload(pl)}
                    disabled={downloadingId === pl.id}
                    className="bg-[#1e2b22] hover:bg-[#2a3a2f] text-white w-full sm:w-auto"
                  >
                    {downloadingId === pl.id ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Downloaden
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-[#fcfaf6] rounded-xl border border-[#e7e1d6] p-8 text-center">
              <FileText className="h-10 w-10 text-[#cfcac2] mx-auto mb-3" />
              <p className="text-[#5a6552]">Er zijn momenteel geen prijslijsten beschikbaar.</p>
              <p className="text-sm text-[#cfcac2] mt-1">Neem contact op met Buitn Planten voor meer informatie.</p>
            </div>
          )}
        </div>

        {/* New order confirmation */}
        {submittedOrder && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-8"
          >
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-green-900 mb-1">Bestelling verzonden!</h3>
                <p className="text-sm text-green-800">
                  Je aanvraag met nummer <span className="font-medium">{submittedOrder}</span> is ontvangen. Wij controleren de voorraad en sturen je een bevestiging per e-mail.
                </p>
                <Button
                  onClick={() => setSubmittedOrder(null)}
                  variant="ghost"
                  size="sm"
                  className="mt-3 text-green-700 hover:bg-green-100"
                >
                  Sluiten
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Order form / Order list toggle */}
        {!showOrderForm ? (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-[#5a6552]" />
                <h2 className="text-lg font-semibold text-[#1e2b22]">Mijn bestellingen</h2>
              </div>
              <Button
                onClick={() => setShowOrderForm(true)}
                className="bg-[#1e2b22] hover:bg-[#2a3a2f] text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nieuwe bestelling
              </Button>
            </div>

            {orders.length === 0 ? (
              <div className="bg-white rounded-2xl border border-[#e7e1d6] p-8 text-center">
                <Package className="h-10 w-10 text-[#cfcac2] mx-auto mb-3" />
                <p className="text-[#5a6552]">Je hebt nog geen bestellingen geplaatst.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {orders.map((order) => (
                  <div key={order.id} className="bg-white rounded-xl border border-[#e7e1d6] p-5">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                      <div>
                        <p className="font-medium text-[#1e2b22]">{order.order_number}</p>
                        <p className="text-sm text-[#5a6552]">{formatDate(order.created_at)}</p>
                      </div>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status)}`}>
                        {getStatusLabel(order.status)}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {order.items.map((item, i) => (
                        <div key={i} className="text-sm text-[#5a6552] flex justify-between">
                          <span>{item.product_name}</span>
                          <span className="font-medium">{item.quantity}x</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-[#e7e1d6] flex gap-4 text-xs text-[#5a6552]">
                      <span className="flex items-center gap-1">
                        {order.delivery_method === 'bezorgen' ? <Truck className="h-3.5 w-3.5" /> : <Store className="h-3.5 w-3.5" />}
                        {order.delivery_method === 'bezorgen' ? 'Bezorgen' : 'Afhalen'}
                      </span>
                      {order.desired_delivery_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          Leverdatum: {formatDate(order.desired_delivery_date)}
                        </span>
                      )}
                      {order.pickup_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          Afhaaldatum: {formatDate(order.pickup_date)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-[#e7e1d6] p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-[#5a6552]" />
                <h2 className="text-lg font-semibold text-[#1e2b22]">Nieuwe bestelling</h2>
              </div>
              <Button
                onClick={() => setShowOrderForm(false)}
                variant="ghost"
                size="sm"
                className="text-[#5a6552]"
              >
                Annuleren
              </Button>
            </div>

            <Alert className="mb-6 bg-[#fcfaf6] border-[#e7e1d6]">
              <AlertDescription className="text-[#5a6552]">
                Let op: dit is een aanvraag ter controle. Wij controleren de voorraad en sturen je een bevestiging per e-mail. Je ontvangt daarna een factuur.
              </AlertDescription>
            </Alert>

            <form onSubmit={handleOrderSubmit} className="space-y-6">
              {/* Order items */}
              <div className="space-y-3">
                {orderItems.map((item, index) => (
                  <div key={index} className="flex flex-col sm:flex-row gap-3 bg-[#fcfaf6] rounded-xl border border-[#e7e1d6] p-4">
                    <div className="flex-1">
                      <Label className="text-xs text-[#5a6552] mb-1 block">Productnaam / artikelnummer</Label>
                      <Input
                        type="text"
                        value={item.product_name}
                        onChange={(e) => updateOrderItem(index, 'product_name', e.target.value)}
                        placeholder="Bijv. Taxus Baccata 100-120cm"
                        className="h-10 bg-white border-[#e7e1d6] text-[#1e2b22] focus:border-[#1e2b22]"
                      />
                    </div>
                    <div className="sm:w-24">
                      <Label className="text-xs text-[#5a6552] mb-1 block">Aantal</Label>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateOrderItem(index, 'quantity', e.target.value)}
                        className="h-10 bg-white border-[#e7e1d6] text-[#1e2b22] focus:border-[#1e2b22]"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs text-[#5a6552] mb-1 block">Opmerking (optioneel)</Label>
                      <Input
                        type="text"
                        value={item.notes}
                        onChange={(e) => updateOrderItem(index, 'notes', e.target.value)}
                        placeholder="Bijv. kale wortel"
                        className="h-10 bg-white border-[#e7e1d6] text-[#1e2b22] focus:border-[#1e2b22]"
                      />
                    </div>
                    {orderItems.length > 1 && (
                      <div className="flex items-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeOrderItem(index)}
                          className="text-red-500 hover:bg-red-50 h-10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={addOrderItem}
                className="border-[#e7e1d6] text-[#5a6552] hover:bg-[#e7e1d6]/50"
              >
                <Plus className="h-4 w-4 mr-2" />
                Product toevoegen
              </Button>

              {/* Delivery method */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-[#1e2b22]">Bezorgmethode</Label>
                <RadioGroup
                  value={deliveryMethod}
                  onValueChange={setDeliveryMethod}
                  className="space-y-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="bezorgen" id="bezorgen" />
                    <Label htmlFor="bezorgen" className="font-normal cursor-pointer flex items-center gap-2">
                      <Truck className="h-4 w-4 text-[#5a6552]" />
                      Bezorgen
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="afhalen" id="afhalen" />
                    <Label htmlFor="afhalen" className="font-normal cursor-pointer flex items-center gap-2">
                      <Store className="h-4 w-4 text-[#5a6552]" />
                      Afhalen in Dedemsvaart
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Date fields */}
              {deliveryMethod === 'bezorgen' ? (
                <div className="space-y-2">
                  <Label htmlFor="delivery_date" className="text-sm font-medium text-[#1e2b22]">
                    Gewenste leverdatum
                  </Label>
                  <Input
                    id="delivery_date"
                    type="date"
                    value={desiredDeliveryDate}
                    onChange={(e) => setDesiredDeliveryDate(e.target.value)}
                    required
                    className="h-11 bg-white border-[#e7e1d6] text-[#1e2b22] focus:border-[#1e2b22]"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="pickup_date" className="text-sm font-medium text-[#1e2b22]">
                    Gewenste afhaaldatum
                  </Label>
                  <Input
                    id="pickup_date"
                    type="date"
                    value={pickupDate}
                    onChange={(e) => setPickupDate(e.target.value)}
                    required
                    className="h-11 bg-white border-[#e7e1d6] text-[#1e2b22] focus:border-[#1e2b22]"
                  />
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="order_notes" className="text-sm font-medium text-[#1e2b22]">
                  Opmerking (optioneel)
                </Label>
                <Textarea
                  id="order_notes"
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                  placeholder="Heb je nog specifieke wensen of vragen?"
                  rows={3}
                  className="resize-none bg-white border-[#e7e1d6] text-[#1e2b22] focus:border-[#1e2b22]"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-[#1e2b22] hover:bg-[#2a3a2f] text-white font-semibold shadow-lg shadow-[#1e2b22]/20"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Verzenden...
                  </>
                ) : (
                  'Bestelling aanvragen'
                )}
              </Button>
            </form>
          </div>
        )}
      </div>
    </>
  );
}
